import Component from '@glimmer/component'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { on } from '@ember/modifier'
import { eq, not } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { classesForTheme } from 'teacher-ui-client/components/copy-classes/themes'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { PanelHeadingIcon } from 'teacher-ui-client/components/base/panel-heading-icon'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'

interface Signature {
  Args: {
    fromSubscriptionType?: SubscriptionType
    toSubscriptionType: SubscriptionType
    updateSubscriptionType: (subscriptionType: SubscriptionType) => void
    reset: () => void
  }
}

export class ProgramPanel extends Component<Signature> {
  @service intl!: IntlService

  @service featureControl!: FeatureControlService

  get themeKey() {
    return subscriptionTypeToThemeKey(this.args.toSubscriptionType)
  }

  get themeClasses() {
    return classesForTheme(this.themeKey)
  }

  get isWritingEnabled() {
    return this.featureControl.canAccess('writing_area')
  }

  get fromSubscriptionType() {
    const isValid = this.subscriptionTypeOptions.some(({ id }) => id === this.args.fromSubscriptionType)
    return isValid ? this.args.fromSubscriptionType : undefined
  }

  get subscriptionTypeOptions() {
    return [
      SubscriptionType.Reading,
      SubscriptionType.Maths,
      ...(this.isWritingEnabled ? [SubscriptionType.Writing] : []),
    ]
      .filter((subscriptionType) => subscriptionType !== this.args.toSubscriptionType)
      .map((subscriptionType) => ({ id: subscriptionType, label: this.intl.t(subscriptionType) }))
  }

  <template>
    <section class="rounded-md border shadow-md">
      <div class="flex items-center justify-between rounded-t-md px-4 py-3 {{this.themeClasses.background.mid}}">
        <h3 class="flex items-center gap-3">
          <PanelHeadingIcon @icon="phosphor-list-bullets-bold" @iconColorClass={{this.themeClasses.text}} />
          <span>{{t "components.copyClasses.programSection.heading"}}</span>
        </h3>
        {{#if this.fromSubscriptionType}}
          <button
            data-test-reset-program-selector
            type="button"
            {{on "click" @reset}}
            class="text-dusty-black-400 font-semibold"
          >{{t "reset"}}</button>
        {{/if}}
      </div>
      <div class="space-y-3 px-8 pt-8 pb-8">
        <label>
          <span class="mb-2 block font-semibold">
            {{t "components.copyClasses.programSection.label"}}
          </span>
          <BaseSelect
            data-test-program-select
            class="w-72 font-normal"
            value={{this.fromSubscriptionType}}
            @themeKey={{this.themeKey}}
            {{on "input" (eventValue @updateSubscriptionType)}}
          >
            <option value="" selected={{not this.fromSubscriptionType}} disabled={{this.fromSubscriptionType}}>{{t
                "components.copyClasses.programSection.placeholder"
              }}</option>
            {{#each this.subscriptionTypeOptions as |subscriptionTypeOption|}}
              <option
                selected={{eq subscriptionTypeOption.id this.fromSubscriptionType}}
                value={{subscriptionTypeOption.id}}
              >{{subscriptionTypeOption.label}}</option>
            {{/each}}
          </BaseSelect>
        </label>
        <span class="flex items-center text-sm {{if this.fromSubscriptionType 'visible' 'invisible'}}">
          <PhosphorIcon @icon="phosphor-info-fill" class="text-oceany-blue-325 mr-1" />
          {{t "components.copyClasses.programSection.warning"}}
        </span>
      </div>
    </section>
  </template>
}
